import React, { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
    Button,
    Paper,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    TableSortLabel,
    Box,
    Typography,
} from "./TailwindMuiStubs";
import { TimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers";
import skLocale from "date-fns/locale/sk"; // Slovak locale
import socket, { connectSocket } from "../../socket";
import BottomBar from "../common/BottomBar"; // Import the BottomBar component

const RaceManagementStart = () => {
    const [crews, setCrews] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState("Všetky");
    const [filterStatus, setFilterStatus] = useState("Všetko");
    const [filterPresence, setFilterPresence] = useState("Prítomní"); // Default to "Prítomní"
    const [selectedCrews, setSelectedCrews] = useState([]);
    const [startTimes, setStartTimes] = useState({}); // Store start times for each crew
    const [sortOrder, setSortOrder] = useState("asc"); // Sorting order: "asc" or "desc"
    const [timeErrors, setTimeErrors] = useState({}); // Track errors for each crew's time
    const { selectedYear } = useOutletContext(); // Global year

    useEffect(() => {
        connectSocket();

        // Fetch crews and categories from the backend
        const fetchCrews = () => {
            socket.emit("fetchCrews", selectedYear);
        };

        const fetchCategories = () => {
            socket.emit("fetchCategories");
        };

        fetchCrews();
        fetchCategories();

        socket.on("crewsData", (data) => {
            setCrews(data);

            // Initialize startTimes state from backend data
            const initialStartTimes = {};
            data.forEach((crew) => {
                if (crew.race_time?.start_time) {
                    initialStartTimes[crew.id] = new Date(crew.race_time.start_time);
                }
            });
            setStartTimes(initialStartTimes);
        });

        socket.on("categoriesData", (data) => {
            setCategories(data);
        });

        socket.on("crewUpdated", (updatedCrew) => {
            setCrews((prevCrews) =>
                prevCrews.map((crew) => (crew.id === updatedCrew.id ? updatedCrew : crew))
            );

            // Update startTimes state for the updated crew
            if (updatedCrew.race_time?.start_time) {
                setStartTimes((prevTimes) => ({
                    ...prevTimes,
                    [updatedCrew.id]: new Date(updatedCrew.race_time.start_time),
                }));
            }
        });

        socket.on("crewsUpdated", (updatedCrews) => {
            setCrews((prevCrews) =>
                prevCrews.map((crew) => {
                    const updatedCrew = updatedCrews.find((updated) => updated.id === crew.id);
                    if (updatedCrew) {
                        // Only update the start_time and race_time fields, keep the rest unchanged
                        return {
                            ...crew,
                            race_time: {
                                ...crew.race_time,
                                start_time: updatedCrew.race_time?.start_time || null,
                            },
                        };
                    }
                    return crew;
                })
            );

            // Update startTimes state for the updated crews
            const updatedStartTimes = {};
            updatedCrews.forEach((crew) => {
                if (crew.race_time?.start_time) {
                    updatedStartTimes[crew.id] = new Date(crew.race_time.start_time);
                }
            });
            setStartTimes((prevTimes) => ({ ...prevTimes, ...updatedStartTimes }));
        });

        return () => {
            socket.off("crewsData");
            socket.off("categoriesData");
            socket.off("crewUpdated");
            socket.off("crewsUpdated");
        };
    }, [selectedYear]);

    const handleCheckboxChange = (crewId) => {
        setSelectedCrews((prevSelected) =>
            prevSelected.includes(crewId)
                ? prevSelected.filter((id) => id !== crewId)
                : [...prevSelected, crewId]
        );
    };

    const handleStartSingle = (crewId) => {
        const startTime = startTimes[crewId] || new Date(); // Use selected time or current time
        socket.emit("startCrew", { crewId, startTime });
    };

    const handleStartMultiple = () => {
        const selectedStartTimes = selectedCrews.map((crewId) => ({
            crewId,
            startTime: startTimes[crewId] || new Date(), // Use selected time or current time
        }));

        socket.emit("startMultipleCrews", { crews: selectedStartTimes }, (response) => {
            if (response.success) {
                // Reload the updated crews from the backend
                socket.emit("fetchCrews", selectedYear);
            } else {
                alert("Failed to start selected crews. Please try again.");
            }
        });

        setSelectedCrews([]); // Clear selection after starting
    };

    const handleTimeChange = (crewId, newTime) => {
        const isInvalid = newTime && isNaN(newTime.getTime());
        const isCleared = !newTime;

        if (isInvalid) {
            setTimeErrors((prevErrors) => ({
                ...prevErrors,
                [crewId]: "Neplatný čas.",
            }));
        } else {
            setTimeErrors((prevErrors) => {
                const { [crewId]: _, ...remainingErrors } = prevErrors;
                return remainingErrors;
            });
        }

        setStartTimes((prevTimes) => ({
            ...prevTimes,
            [crewId]: (isInvalid || isCleared) ? null : newTime,
        }));

        socket.emit("updateCrewField", {
            crewId,
            field: "start_time",
            value: (isInvalid || isCleared) ? null : newTime.toISOString(),
        });
    };

    const handleSortToggle = () => {
        setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
    };

    const handleReset = (crewId) => {
        // Emit the RESET action to the backend
        socket.emit("updateCrewField", { crewId, field: "reset", value: null });

        // Clear the start time in the local state immediately
        setStartTimes((prevTimes) => ({
            ...prevTimes,
            [crewId]: null,
        }));
    };

    const filteredCrews = crews
        .filter((crew) => {
            if (selectedCategory !== "Všetky" && crew.category?.name !== selectedCategory) {
                return false;
            }
            if (filterStatus === "Odštartovaný" && !crew.race_time?.start_time) {
                return false;
            }
            if (filterStatus === "Neodštartovaný" && crew.race_time?.start_time) {
                return false;
            }
            if (filterPresence === "Prítomní" && !crew.present) {
                return false;
            }
            if (filterPresence === "Neprítomní" && crew.present) {
                return false;
            }
            return true;
        })
        .sort((a, b) => {
            if (sortOrder === "asc") {
                return (a.start_number || 0) - (b.start_number || 0);
            } else {
                return (b.start_number || 0) - (a.start_number || 0);
            }
        });

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={skLocale}>
            <div className="mb-6">
                <Typography variant="h4">Štart Maratónu</Typography>
                <Typography variant="body2">
                    Správa štartov posádok podľa kategórií. Hromadné odštartovanie a sledovanie prítomnosti.
                </Typography>
            </div>

            <div style={{ 
                marginBottom: "20px", 
                display: "grid", 
                gridTemplateColumns: "repeat(3, 1fr)", 
                gap: "20px", 
                backgroundColor: "white",
                paddingBottom: "1rem",
                borderBottom: "1px solid #f3f4f6"
            }}>
                <FormControl sx={{ width: "100%" }}>
                    <InputLabel>Prítomnosť</InputLabel>
                    <Select
                        value={filterPresence}
                        onChange={(e) => setFilterPresence(e.target.value)}
                    >
                        <MenuItem value="Prítomní">Prítomní</MenuItem>
                        <MenuItem value="Neprítomní">Neprítomní</MenuItem>
                        <MenuItem value="Všetci">Všetci</MenuItem>
                    </Select>
                </FormControl>
                <FormControl sx={{ width: "100%" }}>
                    <InputLabel>Kategória</InputLabel>
                    <Select
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                    >
                        <MenuItem value="Všetky">Všetky</MenuItem>
                        {categories.map((category) => (
                            <MenuItem key={category.id} value={category.name}>
                                {category.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl sx={{ width: "100%" }}>
                    <InputLabel>Status štartu</InputLabel>
                    <Select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                    >
                        <MenuItem value="Všetko">Všetko</MenuItem>
                        <MenuItem value="Odštartovaný">Odštartovaný</MenuItem>
                        <MenuItem value="Neodštartovaný">Neodštartovaný</MenuItem>
                    </Select>
                </FormControl>
            </div>
            <TableContainer
                component={Paper}
                sx={{
                    overflow: "visible", 
                }}
            >
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <Checkbox
                                    checked={
                                        selectedCrews.length > 0 &&
                                        selectedCrews.length === filteredCrews.length
                                    }
                                    onChange={(e) =>
                                        setSelectedCrews(
                                            e.target.checked
                                                ? filteredCrews.map((crew) => crew.id)
                                                : []
                                        )
                                    }
                                />
                            </TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={true}
                                    direction={sortOrder}
                                    onClick={handleSortToggle}
                                >
                                    Štartovacie číslo
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>Posádka</TableCell>
                            <TableCell>Kategória</TableCell>
                            <TableCell>Štartovací čas</TableCell>
                            <TableCell>Akcia</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredCrews.map((crew) => (
                            <TableRow key={crew.id}>
                                <TableCell>
                                    <Checkbox
                                        checked={selectedCrews.includes(crew.id)}
                                        onChange={() => handleCheckboxChange(crew.id)}
                                    />
                                </TableCell>
                                <TableCell>{crew.start_number || "N/A"}</TableCell>
                                <TableCell>
                                    {crew.participants
                                        ? crew.participants
                                            .map((participant) => participant.name)
                                            .join(", ")
                                        : "N/A"}
                                </TableCell>
                                <TableCell>{crew.category?.name || "N/A"}</TableCell>
                                <TableCell>
                                    <TimePicker
                                        label="Vyberte čas"
                                        value={startTimes[crew.id] || null}
                                        onChange={(newTime) => handleTimeChange(crew.id, newTime)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                error={!!timeErrors[crew.id]} // Highlight field if there's an error
                                                helperText={timeErrors[crew.id]} // Display error message
                                            />
                                        )}
                                        views={["hours", "minutes", "seconds"]}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() => handleStartSingle(crew.id)}
                                        disabled={!!crew.race_time?.start_time} // Disable if already started
                                    >
                                        Štart
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        onClick={() => handleReset(crew.id)}
                                        disabled={!crew.race_time?.start_time} // Disable if no start time
                                        sx={{ marginLeft: 1 }}
                                    >
                                        RESET
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <BottomBar
                leftContent="Štartovať vybrané"
                onLeftButtonClick={handleStartMultiple}
                leftButtonDisabled={selectedCrews.length === 0}
            />
        </LocalizationProvider>
    );
};

export default RaceManagementStart;
